import { useEffect, useState } from 'react'
import { getS3Link } from 'src/utils/helpers'
import {
  useLazyGetNewSessionSignedUrlQuery,
  useGetSessionQuery,
} from 'src/app/appApiQueries'
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'

export function isFetchBaseQueryError(
  error: unknown
): error is FetchBaseQueryError {
  return typeof error === 'object' && error != null && 'status' in error
}

// This hook manages the initial fetch and silent refetch upon certain errors of the active session signed url
export const useSignedUrl = (pollingInterval?: number) => {
  const [link, setLink] = useState(getS3Link())

  const {
    isLoading: s3BucketLoading,
    data,
    error,
  } = useGetSessionQuery(link, { pollingInterval }) // if pollingInterval is undefined, will only fetch once

  const [getNewUrl, { data: newLink }] = useLazyGetNewSessionSignedUrlQuery()

  useEffect(() => {
    // if we'd like to refetch on additional s3 errors, place them here
    // more here: https://docs.aws.amazon.com/AmazonS3/latest/API/ErrorResponses.html
    // ExpiredToken is token expiry with IAMRole
    // AccessDenied is signed url expiry
    if (
      isFetchBaseQueryError(error) &&
      'error' in error &&
      (error.error === 'AccessDenied' || error.error === 'ExpiredToken')
    ) {
      getNewUrl(link)
    }
  }, [error, getNewUrl, link])

  useEffect(() => {
    if (newLink) {
      setLink(newLink)
      // update the cookie in case of page refresh
      document.cookie = `meeting-badges=${encodeURIComponent(newLink)}`
    }
  }, [newLink])

  // this will only show loading upon the initial fetch of
  // once a new link exists, loading will not be re-triggered
  const isLoading = s3BucketLoading && !newLink

  return { isLoading, data }
}
